import { useState, useEffect, createContext, useContext, ReactNode } from 'react';
import { loginService } from '../services/loginService';
import cadastroService from '../services/cadastroService';

// Interfaces
interface User {
  access_token: string;
  refresh_token?: string;
  [key: string]: any;
}

interface LoginCredentials {
  email: string;
  password: string;
  [key: string]: any;
}

interface UserData {
  name?: string;
  email: string;
  password: string;
  [key: string]: any;
}

interface AuthContextType {
  user: User | null;
  isLoggedIn: boolean;
  loading: boolean;
  login: (credentials: LoginCredentials) => Promise<any>;
  logout: () => void;
  register: (userData: UserData) => Promise<any>;
  checkAuth: () => void;
}

interface AuthProviderProps {
  children: ReactNode;
}

// Context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);

  const checkAuth = (): void => {
    const loggedUser = loginService.checkLogged();
    if (loggedUser) {
      setUser(loggedUser);
      setIsLoggedIn(true);
    } else {
      setUser(null);
      setIsLoggedIn(false);
    }
    setLoading(false);
  };

  const login = async (credentials: LoginCredentials): Promise<any> => {
    try {
      const result = await loginService.login(credentials);
      checkAuth();
      return result;
    } catch (error) {
      throw error;
    }
  };

  const register = async (userData: UserData): Promise<any> => {
    try {
      const result = await cadastroService.cadastrar(userData);
      checkAuth();
      return result;
    } catch (error) {
      throw error;
    }
  };

  const logout = (): void => {
    loginService.loggout();
    setUser(null);
    setIsLoggedIn(false);
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const value: AuthContextType = {
    user,
    isLoggedIn,
    loading,
    login,
    logout,
    register,
    checkAuth,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 